/**
 * Settings section catalog — the 3-card index at `/settings`.
 *
 * Shape mirrors `Admin/sections.ts` so the two index surfaces feel
 * like a coherent family — same card grid, same icon + description
 * + quick-list rhythm. Per-section tab definitions DON'T live here;
 * each section's wrapper (`Settings/Account/index.tsx`, etc.) owns
 * its own tab list with the gate metadata. This keeps "what tabs
 * Account has" colocated with "how Account renders," and lets each
 * wrapper test in isolation.
 *
 * The `kind` field drives card-visibility eligibility:
 *   - `'account'`  → always visible to every signed-in user
 *   - `'workspace'`/`'organization'` → visibility derived from the
 *     wrapper's tab list (`some(isVisible)` against the current
 *     role). The index page asks each wrapper "do you have any
 *     visible tabs for this user?" — the card hides when the answer
 *     is no, so an Org Member with no workspace role lands on
 *     Settings and sees only the Account card (#851 Option A).
 *
 * Adding a section is a deliberate decision — not a path of least
 * resistance — per the global "new modules are rare" architecture
 * rule. The expected shape today is 3 cards; if a fourth becomes
 * necessary, add an entry here + write a new wrapper + add a route
 * in `App.tsx`. No other surface should need to learn about it.
 */
import {
  User as UserIcon,
  LayoutGrid,
  Building2,
  type LucideIcon,
} from 'lucide-react'

export type SettingsSectionKind = 'account' | 'workspace' | 'organization'

export interface SettingsSectionChild {
  label: string
  /** Absolute path under `/settings/*`. */
  path: string
}

export interface SettingsSection {
  kind: SettingsSectionKind
  label: string
  /** Absolute path the card click-target navigates to (the section
   * wrapper's landing URL — typically redirects to the first
   * visible tab via the wrapper). */
  path: string
  /** Card subtitle / blurb. Plain-prose copy describing what lives
   * in this section. Mirrors `Admin/sections.ts`'s `description`. */
  description: string
  /** Lucide icon — same library the Sidebar + Admin sections use,
   * so the iconography stays consistent across surfaces. */
  icon: LucideIcon
  /** Tailwind text-color class for the icon. Per-section accent
   * matches the visual language of the Admin landing cards. */
  iconColor: string
  /** Optional quick-list of sub-pages rendered under the
   * description. Telegraphs the tree shape even though the click
   * target is the parent's landing URL. Children are advisory —
   * the actual tab visibility per role is computed by the wrapper,
   * not derived from this list. */
  children?: readonly SettingsSectionChild[]
}

export const SETTINGS_SECTIONS: readonly SettingsSection[] = [
  {
    kind: 'account',
    label: 'Account',
    path: 'account',
    description:
      'Your profile, sign-in methods, access tokens, and notification preferences. Tied to your user, not the workspace.',
    icon: UserIcon,
    iconColor: 'text-indigo-400',
    children: [
      { label: 'Profile',                path: 'account/profile' },
      { label: 'Password & sessions',    path: 'account/password' },
      { label: 'Personal access tokens', path: 'account/tokens' },
      { label: 'Notifications',          path: 'account/notifications' },
      { label: 'Delete account',         path: 'account/danger' },
    ],
  },
  {
    kind: 'workspace',
    label: 'Workspace',
    path: 'workspace',
    description:
      'Settings scoped to the currently-active workspace — members, external integrations.',
    icon: LayoutGrid,
    iconColor: 'text-emerald-400',
    children: [
      { label: 'General',         path: 'workspace/general' },
      { label: 'Members',         path: 'workspace/members' },
      // #1153: the standalone "Notifications" tab and the "CI/CD" tab
      // (the latter renamed from "Integrations" briefly in PR #1145)
      // are consolidated into one "Integrations" tab that lists both
      // the workspace's GitHub and Slack provider connections as
      // sibling cards. The binding controls (per-app repo picker,
      // per-channel routing) moved to the App and Variant tiers
      // (Slices 2 and 3 of meta #1152). Backward compat: App.tsx
      // aliases `workspace/notifications` and `workspace/ci-cd` to
      // `workspace/integrations`; the Settings index's legacy
      // `?tab=workspace.<slug>` regex still forwards through those
      // aliases, so prior bookmarks and OAuth callbacks land correctly.
      { label: 'Integrations',    path: 'workspace/integrations' },
      { label: 'Danger',          path: 'workspace/danger' },
    ],
  },
  {
    kind: 'organization',
    label: 'Organization',
    path: 'organization',
    description:
      'Org-wide configuration — members, teams, workspaces, run budgets, usage, audit log, recovery.',
    icon: Building2,
    iconColor: 'text-sky-400',
    children: [
      { label: 'General',     path: 'organization/general' },
      { label: 'Members',     path: 'organization/members' },
      { label: 'Teams',       path: 'organization/teams' },
      { label: 'Workspaces',  path: 'organization/workspaces' },
      { label: 'Run budgets', path: 'organization/run-budgets' },
      { label: 'Usage',       path: 'organization/usage' },
      { label: 'Audit log',   path: 'organization/audit' },
      { label: 'Recovery',    path: 'organization/recovery' },
      { label: 'Danger',      path: 'organization/danger' },
    ],
  },
]
